import { useMemo } from "react";
import { useAppDownloadPromo } from "@/modules/homeDisplay/hooks";
import { useUserStore } from "@/modules/user/stores";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { triggerPopup } from "@/utils/popupTriggers";
import { useNavigation, ROUTES } from "@/utils/navigation";
import utils from "@/utils";

export interface AppDownloadFloatingIconState {
  shouldShow: boolean;
  imageUrl: string;
  onClick: () => void;
  /** 浮窗倒计时（毫秒）：APP/PWA=领取截止（后台未设置有效期则无）；浏览器=活动有效期（长期有效则无） */
  remainingMs?: number;
}

/**
 * 下载APP激励活动 - 左下角常驻活动浮动图标。
 * 浏览器环境：活动启用期间常驻，点击随时唤起下载激励弹窗（手动触发不受节奏限制）；
 *   已登录用户由后端判断，已达成/已领取则隐藏；未登录用户活动期内始终显示。
 * APP/PWA 环境：
 *   未登录：活动期内常驻，点击引导去登录（登录/注册即达成，领取弹窗按需首冲显示 Deposit/领取态）；
 *   已登录：存在待领取记录时常驻，点击唤起「恭喜获得」领取弹窗，直至领取或过期。
 */
export const useAppDownloadFloatingIcon = (): AppDownloadFloatingIconState => {
  const appDownloadPromo = useAppDownloadPromo();
  const isLoggedIn = useUserStore(state => !!state.userInfo?.userId);
  const { navigate } = useNavigation();
  const isAppOrPwa = useMemo(
    () => utils.isApp() || utils.updateCheckIsPwa(),
    []
  );

  return useMemo(() => {
    const activity = appDownloadPromo?.activity ?? null;
    const pendingClaim = appDownloadPromo?.pendingClaim ?? null;
    const userAchievedOrClaimed =
      appDownloadPromo?.userAchievedOrClaimed === true;

    const hidden: AppDownloadFloatingIconState = {
      shouldShow: false,
      imageUrl: "",
      onClick: () => {},
    };

    const iconResource = activity?.floatingIconImage || "";
    const imageUrl = iconResource ? utils.getImageUrl(iconResource) : "";
    if (!imageUrl || imageUrl === "/not-found.jpg") {
      return hidden;
    }

    if (isAppOrPwa) {
      // APP/PWA 未登录：活动期内常驻浮窗，点击引导登录（登录/注册即达成 →
      // 领取弹窗自动弹出，需首冲显示 Deposit 态、不需要直接显示领取态）
      if (!isLoggedIn) {
        if (!activity) return hidden;
        const activityRemainingMs = activity.effectiveEnd
          ? activity.effectiveEnd - Date.now()
          : undefined;
        return {
          shouldShow: true,
          imageUrl,
          onClick: () => navigate(ROUTES.LOGIN),
          remainingMs:
            activityRemainingMs && activityRemainingMs > 0
              ? activityRemainingMs
              : undefined,
        };
      }
      // APP/PWA 已登录：只要存在未领取（且未过期）的记录就常驻展示，直至领取成功
      const hasPending =
        !!pendingClaim?.recordId &&
        (!pendingClaim.expireAt || pendingClaim.expireAt > Date.now());
      if (!hasPending) return hidden;
      // 领取倒计时：后台设置了领取有效期才显示
      const claimRemainingMs = pendingClaim?.expireAt
        ? pendingClaim.expireAt - Date.now()
        : undefined;
      return {
        shouldShow: true,
        imageUrl,
        onClick: () => triggerPopup(POPUP_CONFIGS.APP_DOWNLOAD_REWARD_CLAIM.id),
        remainingMs:
          claimRemainingMs && claimRemainingMs > 0
            ? claimRemainingMs
            : undefined,
      };
    }

    if (!activity) return hidden;
    if (isLoggedIn && userAchievedOrClaimed) return hidden;
    // 活动有效期倒计时：后台设置了失效时间才显示（长期有效不显示）
    const activityRemainingMs = activity.effectiveEnd
      ? activity.effectiveEnd - Date.now()
      : undefined;
    return {
      shouldShow: true,
      imageUrl,
      onClick: () => triggerPopup(POPUP_CONFIGS.DOWNLOAD_APP.id),
      remainingMs:
        activityRemainingMs && activityRemainingMs > 0
          ? activityRemainingMs
          : undefined,
    };
  }, [appDownloadPromo, isLoggedIn, isAppOrPwa, navigate]);
};
